import { DEV_PORTALS, ONE_HOUR_IN_MS, SVP_PORTALS } from "@/lib/constants";
import { StationData, StationResponse } from "@/lib/types/api/stations-data";
import { devStationId, isDevStation } from "@/lib/station-services/dev-stations";
import uuidv4 from "@/lib/helpers/uuid";
import debug from 'debug'
import { passCacheClientSingleton } from '@/lib/cache/client'
import cachedFetch from "@/lib/helpers/cached-fetch";
import { cacheKey } from "@/lib/helpers/cache-key";

const logger = debug('pbs:station-services:client')

const STATION_SERVICES_API_URL = process.env.STATION_SERVICES_API_URL
const STATION_SERVICES_PASSWORD = process.env.STATION_SERVICES_PASSWORD
const STATION_SERVICES_USERNAME = process.env.STATION_SERVICES_USERNAME

const stationUrl = `${STATION_SERVICES_API_URL}/api/v1/stations/`;

const STATIONS_CACHE_KEY = 'direct_station_data'
const STATION_HOST_CACHE_KEY = 'svp:host'
const STATION_CALLSIGN_CACHE_KEY = 'svp:callsign'

const hostKey = (host: string) => cacheKey(`${STATION_HOST_CACHE_KEY}:${host}`)
const callsignKey = (callsign: string) => cacheKey(`${STATION_CALLSIGN_CACHE_KEY}:${callsign}`)

interface SVPCache {
  timestamp: Date;
  stations: StationData[];
}

class Client {
  endpoint: string;
  objectId: string;
  logPrefix: string;
  authHeaders: Headers;

  constructor () {
    this.objectId = uuidv4()
    this.logPrefix = `${this.objectId.split('-')[0]}`
    this.endpoint = stationUrl

    const headers = new Headers();
    const authHeaderString = `Basic ${Buffer.from(STATION_SERVICES_USERNAME + ':' + STATION_SERVICES_PASSWORD).toString('base64')}`;
    headers.append('Authorization', authHeaderString);
    this.authHeaders = headers
  }

  async cacheClient() {
    return await passCacheClientSingleton()
  }

  log(message: string) {
    logger(`${this.logPrefix} | ${message}`)
  }

  // returns JSON list of all stations from station services
  async getStations() {
    const cache = await this.cacheClient()
    this.log(`Checking cached stations under key ${STATIONS_CACHE_KEY}`)
    const key = cacheKey(STATIONS_CACHE_KEY)
    const cachedStations: StationData[] = await cache.get(key)

    if(cachedStations) {
      return cachedStations
    }

    this.log('Cached Station Data Expired. Refetching...')
    let stations: StationData[] = [];
    let nextPage: string | null = stationUrl;
    while (nextPage !== null) {
      const response = await fetch(nextPage, { method: 'GET', headers: this.authHeaders });
      const json = await response.json() as StationResponse;
      stations = stations.concat(json.data);
      nextPage = json.links.next;
    }

    await this.populatePartitionedFullStationsCache(stations)
    this.log(`Station Data Cache Refilled with ${stations.length} stations`)
    return stations
  }

  async populatePartitionedFullStationsCache(stations: StationData[]) {
    const cache = await this.cacheClient()
    await cache.set(cacheKey(STATIONS_CACHE_KEY), stations, ONE_HOUR_IN_MS, { allowGraduatedResponse: true })

    stations.forEach((station) => {
      this.log(`Iterating over ${station.attributes.call_sign}`)
      const host = this.getPortalHostnameForStation(station)
      const callsign = station.attributes?.call_sign.toLowerCase()

      if(host) {
        const key = hostKey(host)
        cache.set(key, station, ONE_HOUR_IN_MS, { allowGraduatedResponse: true })
        this.log(`Set Station Data for Hostname under ${key}`)
      }

      if(callsign) {
        const key = callsignKey(callsign)
        cache.set(key, station, ONE_HOUR_IN_MS, { allowGraduatedResponse: true })
        this.log(`Set Station Data for Callsign under ${key}`)
      }
    })
  }

  getPortalHostnameForStation(station: StationData) {
    const videoPortal = station.attributes?.video_portal_url

    if (videoPortal) {
      return new URL(videoPortal).host;
    } else {
      return null;
    }
  }

  // get full data object for an individual station by hostname
  async getStationDataByHostName(hostname: string): Promise<StationData | undefined> {
    const cache = await this.cacheClient()
    const key = hostKey(hostname)
    const cachedStationData: StationData = await cache.get(key)
    if(cachedStationData) {
      this.log(`Hit on Cached Station Data by Hostname ${key}`)
      return cachedStationData
    }

    this.log(`Miss on Cached Station Data by Hostname ${key}`)

    const stations = await this.getStations();

    if (isDevStation(hostname)) {
      // we have station IDs hardcoded for dev stations
      const stationId = devStationId(hostname);
      const stationData = stations.find((station) =>
        station.id === stationId
      );
      return stationData
    } else {
      return stations.find((station) =>
        this.getPortalHostnameForStation(station) === hostname
      );
    }
  }

  async getStationDataByCallsign(call_sign: string): Promise<StationData | undefined> {
    const cache = await this.cacheClient()
    const key = callsignKey(call_sign)
    const cachedStationData: StationData = await cache.get(key)
    if(cachedStationData) {
      this.log(`Hit on Cached Station Data by Callsign ${key}`)
      return cachedStationData
    }

    this.log(`Miss on Cached Station Data by Callsign ${key}`)

    const stations = await this.getStations();
    return stations.find((station) =>
      station.attributes?.call_sign.toLowerCase() === call_sign.toLowerCase()
    );
  }

  // get full data object for an individual station by id
  async getStationDataByStationId(stationId: string): Promise<StationData | undefined> {
    const url = `${stationUrl}${stationId}`;
    const json = await cachedFetch(url, ONE_HOUR_IN_MS, { method: 'GET', headers: this.authHeaders });
    return json.data;
  }

  // get station ID for an individual station by hostname
  async getStationIdByHostname(hostname: string): Promise<string | undefined> {
    if (isDevStation(hostname)) {
      return devStationId(hostname);
    }

    const station = await this.getStationDataByHostName(hostname);

    return station?.id;
  }
}

const stationServices = new Client()

export default stationServices;
